import {
  Button,
  Grid2 as Grid,
  MenuItem,
  TextField,
  Typography,
  useTheme,
} from '@mui/material'
import useCustomStyles from '../../../hooks/CustomStylesHook'
import Colors from '../../../utils/colors.json'
import { useFormik } from 'formik'
import * as Yup from 'yup'
import dayjs from 'dayjs'
import { COMPARISON_FIELDS, FIELD_TYPES } from '../../../constants/constants'
import PolicyDetailsCard from './PolicyDetailsCard'

const styles = (theme: any) => ({
  sectionHeading: {
    fontSize: '16px',
    fontWeight: 400,
    color: Colors.TEXT_COLOR_SECONDARY,
  },

  fieldLabel: {
    fontSize: '14px',
    fontWeight: 400,
    color: Colors.TEXT_COLOR_QUATERNARY,
  },

  formTextField: {
    fontSize: '14px',
    fontWeight: 400,
    color: Colors.TEXT_COLOR_SECONDARY,
    height: 40,
    borderRadius: '8px',
  },
  formSelectField: {
    fontSize: '14px',
    fontWeight: 400,
    color: Colors.TEXT_COLOR_SECONDARY,
    minWidth: '180px',
    height: 40,
    borderRadius: '8px',
  },
  formCheckBox: {
    fontSize: '14px',
    fontWeight: 400,
    color: Colors.TEXT_COLOR_SECONDARY,
    border: `1px solid ${Colors.BORDER_COLOR}`,
  },
  borderBox: {
    border: `1px solid ${Colors.BORDER_COLOR}`,
    borderRadius: '20px',
    paddingLeft: 2,
    paddingRight: 7,
  },
  actionBtn: {
    borderRadius: '20px',
    textTransform: 'capitalize',
    minWidth: '180px',
    background: theme.customColors.button,
  },
  actionBtnBack: {
    borderRadius: '20px',
    textTransform: 'capitalize',
    minWidth: '180px',
    background: theme.customColors.btnBlack,
  },
})

const Proposer = ({
  activeStep,
  setActiveStep,
}: {
  activeStep: number
  setActiveStep: (activeStep: number) => void
}) => {
  const theme = useTheme()
  const classes = useCustomStyles(styles, theme)

  // const firstSection = [
  //   { relation: 'Self', proposer: 'Joel Mihavel' },
  //   { relation: 'Mother', proposer: 'Marina' },
  //   { relation: 'Son', proposer: 'Rahul' },
  // ]

  const secondSection = [
    { label: 'Full Name', name: 'fullName', type: 'text' },
    {
      label: 'Gender',
      name: 'gender',
      type: 'select',
      options: [
        { label: 'Male', value: 'male' },
        { label: 'Female', value: 'female' },
      ],
    },
    { label: 'Dob.', name: 'dob', type: 'date' },
    { label: 'Age', name: 'age', type: 'number' },
    {
      label: 'Occupation',
      name: 'occupation',
      type: 'select',
      options: [
        { label: 'Software Engineer', value: 'software_engineer' },
        { label: 'Project Manager', value: 'project_manager' },
      ],
    },
    { label: 'Pan Card No.', name: 'panCard', type: 'text' },
    {
      label: 'Height',
      name: 'height',
      type: 'select',
      options: Array.from({ length: (12 - 3) * 12 + 1 }, (_, i) => {
        const feet = Math.floor(i / 12) + 3 // Start at 3 feet
        const inches = i % 12

        // Convert feet and inches to centimeters
        const cm = Math.round(feet * 30.48 + inches * 2.54)

        // Return object with label and value
        return {
          label: `${feet}ft - ${inches}inch`,
          value: cm,
        }
      }),
    },
    {
      label: 'Weight',
      name: 'weight',
      type: 'select',
      options: Array.from({ length: 100 }, (_, i) => ({
        label: `${i + 1} kg`,
        value: i + 1,
      })),
    },
  ]

  const thridSection = [
    { label: 'Flat / House No., Apartment', name: 'flatNo' },
    { label: 'Colony, Street, Sector', name: 'street' },
    { label: 'City', name: 'city' },
    { label: 'State', name: 'state' },
    { label: 'Pin Code', name: 'pinCode' },
  ]

  const fourthSection = [
    { label: 'Email Address', name: 'emailId' },
    { label: 'Contact No.', name: 'phoneNumber' },
    { label: 'Alternative Contact Number', name: 'alternativeContactNumber' },
    { label: 'Emergency Contact Number', name: 'emergencyContactNumber' },
  ]

  // const ageOptions = [
  //   { label: 'Male', value: 'male' },
  //   { label: 'Female', value: 'female' },
  // ]

  const ERROR_MESSAGES = {
    REQUIRED: 'Required',
    INVALID_EMAIL: 'Invalid email',
    INVALID_DATE: 'Invalid date',
    FUTURE_DATE: 'Date cannot be in the future',
  }

  const formik: any = useFormik({
    initialValues: {
      selectedProposer: '',
      gender: '',
      fullName: '',
      panCard: '',
      flatNo: '',
      street: '',
      city: '',
      state: '',
      pinCode: '',
      emailId: '',
      phoneNumber: '',
      emergencyContactNumber: '',
      alternativeContactNumber: '',
      weight: null,
      height: null,
      age: null,
      dob: '',
    },
    validationSchema: Yup.object({
      proposerGender: Yup.string().required(ERROR_MESSAGES.REQUIRED),
      fullName: Yup.string().required(ERROR_MESSAGES.REQUIRED),
      gender: Yup.string().required(ERROR_MESSAGES.REQUIRED),
      panCard: Yup.string().required(ERROR_MESSAGES.REQUIRED),
      flatNo: Yup.string().required(ERROR_MESSAGES.REQUIRED),
      street: Yup.string().required(ERROR_MESSAGES.REQUIRED),
      city: Yup.string().required(ERROR_MESSAGES.REQUIRED),
      state: Yup.string().required(ERROR_MESSAGES.REQUIRED),
      pinCode: Yup.string().required(ERROR_MESSAGES.REQUIRED),
      emailId: Yup.string()
        .email(ERROR_MESSAGES.INVALID_EMAIL)
        .required(ERROR_MESSAGES.REQUIRED),
      phoneNumber: Yup.string().required(ERROR_MESSAGES.REQUIRED),
      emergencyContactNumber: Yup.string().required(ERROR_MESSAGES.REQUIRED),
      alternativeContactNumber: Yup.string().required(ERROR_MESSAGES.REQUIRED),
      weight: Yup.number().required(ERROR_MESSAGES.REQUIRED),
      height: Yup.number().required(ERROR_MESSAGES.REQUIRED),
      age: Yup.number().required(ERROR_MESSAGES.REQUIRED),
      dob: Yup.date()
        .required(ERROR_MESSAGES.REQUIRED)
        .max(new Date(), ERROR_MESSAGES.FUTURE_DATE),
    }),
    onSubmit: (values) => {
      console.log(values)
      alert('Form submitted successfully!')
    },
  })

  const calculateAge = (dob: string) => {
    const birthDate = dayjs(dob)
    const today = dayjs()

    // Calculate age by subtracting the years
    let age = today.year() - birthDate.year()

    // Adjust age if the birthday hasn't occurred yet this year
    if (today.isBefore(birthDate.add(age, 'year'))) {
      age -= 1
    }

    return age
  }

  const calculateDob = (age: number) => {
    const today = dayjs()
    const birthYear = today.year() - age
    const birthDate = today.set('year', birthYear)

    // Adjust DOB if the birthday hasn't occurred yet this year
    if (today.isBefore(birthDate)) {
      return birthDate.subtract(1, 'year').format('YYYY-MM-DD')
    }
    console.log()
    return birthDate.format('YYYY-MM-DD')
  }

  const handleSecondSectionChange = (e: any) => {
    if (e.target.name === COMPARISON_FIELDS?.DOB) {
      formik?.handleChange(e)
      const age = calculateAge(e?.target?.value)
      formik?.setFieldValue(COMPARISON_FIELDS?.AGE, age)
    } else if (e.target.name === COMPARISON_FIELDS?.AGE) {
      formik?.handleChange(e)
      const dob = calculateDob(e?.target?.value)
      formik?.setFieldValue(COMPARISON_FIELDS?.DOB, dob)
    } else {
      formik?.handleChange(e)
    }
  }

  return (
    <Grid container direction={'column'} spacing={4} pl={2} pb={4}>
      <PolicyDetailsCard />
      {/* <Grid container spacing={2.5}>
        <Grid size='auto'>
          <Typography className={classes?.sectionHeading}>
            Select Proposers Gender
          </Typography>
        </Grid>
        <Grid container size={{ xs: 12 }} spacing={3}>
          {ageOptions?.map((gender, index) => (
            <Grid size='auto' key={index}>
              <FormControlLabel
                className={classes?.borderBox}
                control={<Checkbox size='small' />}
                label={gender?.label}
                checked={formik.values.proposerGender === gender?.value}
                onChange={() =>
                  formik.setFieldValue('proposerGender', gender?.value)
                }
              />
            </Grid>
          ))}
        </Grid>
      </Grid> */}
      {/* <Grid container spacing={2.5}>
        <Grid size='auto'>
          <Typography className={classes?.sectionHeading}>
            Select Proposer
          </Typography>
        </Grid>
        <Grid container size={{ xs: 12 }} spacing={3}>
          {firstSection?.map((item, index) => (
            <Grid
              container
              direction={'column'}
              size='auto'
              spacing={1}
              key={index}
            >
              <Grid size='auto'>
                <Typography className={classes?.fieldLabel}>
                  {item?.relation}
                </Typography>
              </Grid>
              <Grid size='auto'>
                <FormControlLabel
                  className={classes?.borderBox}
                  control={<Checkbox size='small' />}
                  label={item?.proposer}
                  checked={formik.values.selectedProposer === item?.proposer}
                  onChange={() =>
                    formik.setFieldValue('selectedProposer', item?.proposer)
                  }
                />
              </Grid>
            </Grid>
          ))}
        </Grid>
      </Grid> */}
      <Grid container spacing={2.5}>
        <Grid size='auto'>
          <Typography className={classes?.sectionHeading}>
            Proposer’s Detail
          </Typography>
        </Grid>
        <Grid container size={{ xs: 12 }} spacing={3}>
          {secondSection?.map((item, index) => (
            <Grid
              container
              direction={'column'}
              size={{ xs: 12, sm: 6, lg: 3, md: 3, xl: 3 }}
              spacing={1}
              key={index}
            >
              <Grid size='auto'>
                <Typography className={classes?.fieldLabel}>
                  {item?.label}
                </Typography>
              </Grid>
              <Grid size='auto'>
                <TextField
                  name={item?.name}
                  value={formik?.values?.[item?.name] || ''}
                  onChange={(e) => handleSecondSectionChange(e)}
                  onBlur={formik.handleBlur}
                  error={
                    formik?.touched?.[item?.name] &&
                    Boolean(formik?.errors?.[item?.name])
                  }
                  helperText={
                    formik?.touched?.[item?.name] &&
                    formik?.errors?.[item?.name]
                  }
                  slotProps={{
                    input: {
                      className:
                        item?.type === FIELD_TYPES?.SELECT
                          ? classes?.formSelectField
                          : classes?.formTextField,
                    },
                  }}
                  fullWidth
                  type={
                    item?.type !== FIELD_TYPES?.SELECT ? item?.type : undefined
                  }
                  select={item?.type === FIELD_TYPES?.SELECT}
                >
                  {item?.type === FIELD_TYPES?.SELECT &&
                    item?.options?.map((option, index) => (
                      <MenuItem key={index} value={option?.value}>
                        {option?.label}
                      </MenuItem>
                    ))}
                </TextField>
                {item?.name === COMPARISON_FIELDS.HEIGHT && (
                  <TextField
                    type='number'
                    name={item?.name}
                    onChange={formik.handleChange}
                    value={formik?.values?.[item?.name]}
                    sx={{ mt: 1, float: 'right' }}
                    placeholder='Enter Height In Cms'
                    slotProps={{
                      input: {
                        className: classes?.formTextField,
                        endAdornment: 'Cm',
                      },
                    }}
                  />
                )}
                {/* {item?.name === COMPARISON_FIELDS.WEIGHT && (
                  <TextField
                    type='number'
                    name={item?.name}
                    onChange={formik.handleChange}
                    value={formik?.values?.[item?.name]}
                    sx={{ mt: 1, float: 'right' }}
                    placeholder='Enter Weight'
                    slotProps={{
                      input: {
                        className: classes?.formTextField,
                        endAdornment: 'KG',
                      },
                    }}
                  />
                )} */}
              </Grid>
            </Grid>
          ))}
        </Grid>
      </Grid>
      <Grid container spacing={2.5}>
        <Grid size='auto'>
          <Typography className={classes?.sectionHeading}>Address</Typography>
        </Grid>
        <Grid container spacing={3} size={{ xs: 12 }}>
          {thridSection?.map((item, index) => (
            <Grid
              container
              direction={'column'}
              size={{ xs: 6, sm: 6, lg: 4, md: 4, xl: 4 }}
              spacing={1}
              key={index}
            >
              <Grid size='auto'>
                <Typography className={classes?.fieldLabel}>
                  {item?.label}
                </Typography>
              </Grid>
              <Grid size='auto'>
                <TextField
                  name={item?.name}
                  value={formik?.values?.[item?.name]}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={
                    formik?.touched?.[item?.name] &&
                    Boolean(formik.errors?.[item?.name])
                  }
                  helperText={
                    formik.touched?.[item?.name] && formik.errors?.[item?.name]
                  }
                  slotProps={{
                    input: {
                      className: classes?.formTextField,
                    },
                  }}
                  fullWidth
                />
              </Grid>
            </Grid>
          ))}
        </Grid>
      </Grid>
      <Grid container spacing={2.5}>
        <Grid size='auto'>
          <Typography className={classes?.sectionHeading}>
            Contact Details
          </Typography>
        </Grid>
        <Grid container spacing={3} size={{ xs: 12 }}>
          {fourthSection?.map((item, index) => (
            <Grid
              container
              direction={'column'}
              size={{ xs: 6, sm: 6, lg: 4, md: 4, xl: 4 }}
              spacing={1}
              key={index}
            >
              <Grid size='auto'>
                <Typography className={classes?.fieldLabel}>
                  {item?.label}
                </Typography>
              </Grid>
              <Grid size='auto'>
                <TextField
                  name={item?.name}
                  value={formik?.values?.[item?.name]}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={
                    formik?.touched?.[item?.name] &&
                    Boolean(formik.errors?.[item?.name])
                  }
                  helperText={
                    formik.touched?.[item?.name] && formik.errors?.[item?.name]
                  }
                  slotProps={{
                    input: {
                      className: classes?.formTextField,
                    },
                  }}
                  fullWidth
                />
              </Grid>
            </Grid>
          ))}
        </Grid>
      </Grid>
      <Grid size={{ xs: 12 }} display={'flex'} justifyContent={'space-around'}>
        <Button
          variant='contained'
          // color='primary'
          className={classes?.actionBtnBack}
          onClick={() => setActiveStep(activeStep - 1)}
          disabled={activeStep === 0}
        >
          Back
        </Button>
        <Button
          variant='contained'
          // color='primary'
          className={classes?.actionBtn}
          onClick={() => setActiveStep(activeStep + 1)}
        >
          Proceed To Members
        </Button>
      </Grid>
    </Grid>
  )
}

export default Proposer
